/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * usePublicViewConfig — fetch and mutate the PublicTableView config for a table.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Fetch existing config (null if not yet configured).
 */
export function usePublicViewConfig(tableId) {
  return useQuery({
    queryKey: ['publicViewConfig', tableId],
    queryFn: async ({ signal }) => {
      if (!tableId) return null;
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/${tableId}/public-view/config/`,
        { method: 'GET', signal }
      );
      if (resp.status === 404) return null;
      if (!resp.ok) {
        let body = {};
        try { body = await resp.json(); } catch (_) {}
        throw new Error(body.error || body.message || `Failed to load config (${resp.status})`);
      }
      return resp.json();
    },
    enabled: !!tableId,
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Save (upsert) config for a table. Returns the saved config on success.
 */
export async function savePublicViewConfig(tableId, configPatch) {
  const resp = await apiHelper.apiCall(
    `/arcee/application/table/public-view/configure/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ table_id: tableId, ...configPatch }),
    }
  );
  if (!resp.ok) {
    let body = {};
    try { body = await resp.json(); } catch (_) {}
    throw new Error(body.error || body.message || `Save failed (${resp.status})`);
  }
  return resp.json();
}

/**
 * Rotate the public access key for a table.
 */
export async function rotatePublicAccessKey(tableId) {
  const resp = await apiHelper.apiCall(
    `/arcee/application/table/${tableId}/public-view/rotate-key/`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } }
  );
  if (!resp.ok) {
    let body = {};
    try { body = await resp.json(); } catch (_) {}
    throw new Error(body.error || body.message || `Rotate key failed (${resp.status})`);
  }
  return resp.json();
}

/**
 * Fetch index page config for an application.
 */
export function usePublicIndexConfig(appId) {
  return useQuery({
    queryKey: ['publicIndexConfig', appId],
    queryFn: async ({ signal }) => {
      if (!appId) return {};
      const resp = await apiHelper.apiCall(
        `/arcee/application/public-index/config/?application_id=${appId}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return {};
      const body = await resp.json();
      // Unwrap nested public_index_config if present
      return body.public_index_config || body;
    },
    enabled: !!appId,
    staleTime: 30_000,
  });
}

/**
 * Save index page config.
 */
export async function savePublicIndexConfig(config) {
  const resp = await apiHelper.apiCall(
    `/arcee/application/public-index/configure/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }
  );
  if (!resp.ok) {
    let body = {};
    try { body = await resp.json(); } catch (_) {}
    throw new Error(body.error || body.message || `Save index config failed (${resp.status})`);
  }
  return resp.json();
}
